import getRealtimePreScreenDetails from 'services/api/profile/getRealtimePreScreenDetails';
import profileApi from 'services/api/profile/index';
import localeUtils from 'utils/LanguageLocale';
import Storage from 'utils/localStorage/Storage';
import userUtils from 'utils/User';
import UtilActions from 'utils/redux/Actions';
import localStorageConstants from 'utils/localStorage/Constants';
import updateBiAccount from 'services/api/beautyInsider/updateBiAccount';
import prescreenCustomerResponse from 'services/api/profile/prescreenCustomerResponse';
import creditCard from 'reducers/creditCard';
import RCPSCookies from 'utils/RCPSCookies';
import getRealtimePrescreenDetailsSDN from 'services/api/profile/getRealtimePrescreenDetailsSDN';
import stringUtils from 'utils/String';

const { ACTION_TYPES } = creditCard;

const { CREDIT_CARD_REALTIME_PRESCREEN, CREDIT_CARD_TARGETERS } = localStorageConstants;
const { StorageTypes } = Storage;
const REALTIME_PRESCREEN_CONFIG = {
    INTERVAL_IN_SECONDS: 30,
    MAX_COUNT: 6,
    FIRST_CALL: 1
};

// 10 minutes cache
const CREDIT_CARD_TARGETERS_CACHE_TIME = 1000 * 60 * 10;
const config = {
    cache: {
        key: CREDIT_CARD_TARGETERS,
        expiry: CREDIT_CARD_TARGETERS_CACHE_TIME,
        storageType: StorageTypes.IndexedDB
    }
};

const CC_BASKET_TARGETER = '/atg/registry/RepositoryTargeters/Sephora/CCDynamicMessagingBasketTargeter';
const CC_INLINE_BASKET_TARGETER = '/atg/registry/RepositoryTargeters/Sephora/CCDynamicMessagingInlineBasketTargeter';
const CC_CHECKOUT_TARGETER = '/atg/registry/RepositoryTargeters/Sephora/CCDynamicMessagingCheckoutTargeter';
const targeters = [CC_BASKET_TARGETER, CC_INLINE_BASKET_TARGETER, CC_CHECKOUT_TARGETER];

// Track the active polling interval
let realtimePrescreenPollingInterval = null;

const creditCardActions = {
    handleRealtimePrescreen: function (loyaltyId, ccAccountandPrescreenInfo) {
        if (localeUtils.isUS()) {
            const storage = Storage.local.getItem(CREDIT_CARD_REALTIME_PRESCREEN);

            if (!storage) {
                // If there is no storage yet, attempt to initiate the process
                // Only set storage if RCPSCookies.isRCPSCCAP() is true to avoid stuck inProgress state

                // Fast path: If realTimePrescreenInProgress is true (record already exists in DB),
                // make a single poll that will return "Complete" immediately
                if (ccAccountandPrescreenInfo && ccAccountandPrescreenInfo?.realTimePrescreenInProgress === true && RCPSCookies.isRCPSCCAP()) {
                    creditCardActions.updateStorage(CREDIT_CARD_REALTIME_PRESCREEN, {
                        count: 0,
                        inProgress: true
                    });

                    creditCardActions.makeRealtimePrescreenCall(loyaltyId, REALTIME_PRESCREEN_CONFIG.FIRST_CALL);
                } else if (ccAccountandPrescreenInfo && ccAccountandPrescreenInfo?.userPrescreenable === true && RCPSCookies.isRCPSCCAP()) {
                    // New polling trigger: If userPrescreenable is true (no prescreen record yet, ADS call in flight),
                    // begin timer-based polling on the current page
                    creditCardActions.updateStorage(CREDIT_CARD_REALTIME_PRESCREEN, {
                        count: 0,
                        inProgress: true
                    });

                    // Start timer-based polling with 30s intervals (only if not already running)
                    creditCardActions.startRealtimePrescreenPolling(loyaltyId);
                }
            } else {
                // If there is storage, check if we should resume polling

                // If there is a user response already, or if we have a cardType, stop
                if (RCPSCookies.isRCPSCCAP() && storage.response?.cardType) {
                    creditCardActions.stopRealtimePrescreenPolling();

                    return;
                }

                if (!RCPSCookies.isRCPSCCAP() && (storage.userResponse || !storage.inProgress)) {
                    return;
                }

                // If max count was reached, stop polling completely
                if (storage.count >= REALTIME_PRESCREEN_CONFIG.MAX_COUNT && RCPSCookies.isRCPSCCAP()) {
                    creditCardActions.stopRealtimePrescreenPolling();
                    creditCardActions.updateStorage(CREDIT_CARD_REALTIME_PRESCREEN, {
                        inProgress: false
                    });

                    return;
                } else if (storage.inProgress && storage.count < REALTIME_PRESCREEN_CONFIG.MAX_COUNT) {
                    // Resume polling if still in progress and count < MAX_COUNT
                    // Only start if not already running
                    if (RCPSCookies.isRCPSCCAP() && !realtimePrescreenPollingInterval) {
                        // Resume timer-based polling from current count
                        creditCardActions.startRealtimePrescreenPolling(loyaltyId);
                    }
                }
            }
        }
    },

    startRealtimePrescreenPolling: function (loyaltyId) {
        // If polling is already active, don't restart (prevents API spam from repeated profile refreshes)
        if (realtimePrescreenPollingInterval) {
            return;
        }

        // Make the first poll immediately
        const storage = Storage.local.getItem(CREDIT_CARD_REALTIME_PRESCREEN);
        const currentCount = storage?.count || 0;

        // Fire first poll immediately
        creditCardActions.makeRealtimePrescreenCall(loyaltyId, currentCount + 1);

        // Set up interval for subsequent polls every 30 seconds
        realtimePrescreenPollingInterval = setInterval(() => {
            const storageCheck = Storage.local.getItem(CREDIT_CARD_REALTIME_PRESCREEN);

            // Stop conditions:
            // 1. Max count reached
            // 2. No longer in progress (response complete)
            // 3. User responded
            // 4. Response has cardType (RCPS flow)
            if (
                !storageCheck ||
                storageCheck.count >= REALTIME_PRESCREEN_CONFIG.MAX_COUNT ||
                !storageCheck.inProgress ||
                storageCheck.userResponse ||
                storageCheck.response?.cardType
            ) {
                creditCardActions.stopRealtimePrescreenPolling();

                return;
            }

            // Make next poll
            creditCardActions.makeRealtimePrescreenCall(loyaltyId, storageCheck.count + 1);
        }, REALTIME_PRESCREEN_CONFIG.INTERVAL_IN_SECONDS * 1000);
    },

    stopRealtimePrescreenPolling: function () {
        if (realtimePrescreenPollingInterval) {
            clearInterval(realtimePrescreenPollingInterval);
            realtimePrescreenPollingInterval = null;
        }
    },

    updateStorage: function (key, data) {
        const storage = Object.assign({}, Storage.local.getItem(key), data);
        Storage.local.setItem(key, storage, userUtils.USER_DATA_EXPIRY);
    },

    makeRealtimePrescreenCall: function (loyaltyId, count) {
        // Makes the LightWeight API call
        const apiCall = RCPSCookies.isRCPSCCAP() ? getRealtimePrescreenDetailsSDN : getRealtimePreScreenDetails;

        return apiCall(loyaltyId)
            .then((response = {}) => {
                const inProgress = response.preScreenProcessStatus && response.preScreenProcessStatus.toLowerCase() === 'in progress';
                const complete = response.preScreenProcessStatus && response.preScreenProcessStatus.toLowerCase() === 'complete';
                const approved = response.preScreenApprovalStatus && response.preScreenApprovalStatus.toLowerCase() === 'approved';
                const isValidCardType = stringUtils.isValidString(response?.cardType);

                creditCardActions.updateLightweightCallDetails({
                    count,
                    inProgress,
                    response
                });

                // Stop polling if we received a final result or reached max count
                if (!inProgress || count >= REALTIME_PRESCREEN_CONFIG.MAX_COUNT) {
                    creditCardActions.stopRealtimePrescreenPolling();
                }

                if (complete && approved && isValidCardType) {
                    // Use dynamic import to avoid circular dependency
                    // Use isSpaLoad=true since polling happens mid-session
                    import(/* webpackMode: "eager" */ 'services/PostLoad').then(module => {
                        module.default.showCreditCardPrescreenModal(true);
                        // Mark modal as shown AFTER rendering to prevent duplicate display on next page load
                        creditCardActions.updateLightweightCallDetails({
                            count,
                            inProgress,
                            response,
                            modalShown: true
                        });
                    });
                }
            })
            .catch(() => {
                creditCardActions.updateLightweightCallDetails({ count });

                // Stop polling on error if max count reached
                if (count >= REALTIME_PRESCREEN_CONFIG.MAX_COUNT) {
                    creditCardActions.stopRealtimePrescreenPolling();
                }
            });
    },

    updateLightweightCallDetails: function (data = {}) {
        const storage = {
            timestamp: Date.now(),
            ...data
        };

        creditCardActions.updateStorage(CREDIT_CARD_REALTIME_PRESCREEN, storage);
    },

    captureRealtimePrescreenUserResponse: function (userResponse) {
        // Stop polling when user responds
        creditCardActions.stopRealtimePrescreenPolling();

        const profileData = {
            profileId: userUtils.getProfileId(),
            biAccount: { prescreenCustomerResponse: userResponse }
        };

        return updateBiAccount(profileData).then(response => {
            let promise;

            if (response.responseStatus === 200) {
                creditCardActions.updateStorage(CREDIT_CARD_REALTIME_PRESCREEN, { userResponse });
                promise = Promise.resolve(response);
            } else {
                promise = Promise.reject(response);
            }

            return promise;
        });
    },

    realtimePrescreenUserResponse: function (userResponse) {
        // Stop polling when user responds
        creditCardActions.stopRealtimePrescreenPolling();

        const biAccount = userUtils.getBiAccountInfo();

        return prescreenCustomerResponse(biAccount?.biAccountId, userResponse).then(response => {
            let promise;

            if (response.responseStatus === 200) {
                creditCardActions.updateStorage(CREDIT_CARD_REALTIME_PRESCREEN, { userResponse });
                promise = Promise.resolve(response);
            } else {
                promise = Promise.reject(response);
            }

            return promise;
        });
    },

    getCreditCardTargeters: function (force = false) {
        return dispatch => {
            if (Sephora.configurationSettings?.isTargetersATGSunset) {
                return Promise.resolve();
            }

            return profileApi.getTargetersContent(targeters, { ...config, cache: { ...config.cache, invalidate: force } }).then(response => {
                if (response.responseStatus === 200 && response.targeterResult) {
                    const ccTargeters = {
                        CCDynamicMessagingBasketTargeter: response.targeterResult[CC_BASKET_TARGETER],
                        CCDynamicMessagingInlineBasketTargeter: response.targeterResult[CC_INLINE_BASKET_TARGETER],
                        CCDynamicMessagingCheckoutTargeter: response.targeterResult[CC_CHECKOUT_TARGETER]
                    };

                    return dispatch(UtilActions.merge('creditCard', 'ccTargeters', ccTargeters));
                } else {
                    return dispatch(UtilActions.merge('creditCard', 'ccTargeters', {}));
                }
            });
        };
    },

    updateCCBanner: function (ccBanner) {
        return {
            type: ACTION_TYPES.UPDATE_CC_BANNER,
            ccBanner
        };
    }
};

export default creditCardActions;
